<script lang="ts" module>
	export interface Badge {
		color: string;
		index: number;
		subIndex?: string;
	}
</script>

<script lang="ts">
	import type { SecondarySource } from '#src/types/sources.ts';
	import type { Component } from 'svelte';
	import IconCompany from 'virtual:icons/material-symbols/corporate-fare';
	import IconDocument from 'virtual:icons/material-symbols/description';
	import IconPoll from 'virtual:icons/material-symbols/how-to-vote';
	import IconGlobe from 'virtual:icons/material-symbols/language';
	import IconIfgRequest from 'virtual:icons/material-symbols/mail';
	import IconPerson from 'virtual:icons/material-symbols/person';
	import { darkenColor } from '../util/colors';
	import { getWindowContext } from '#src/contexts/window.context.svelte.ts';
	interface Props {
		badge: Badge;
		/** Set for secondary sources — picks the type-specific icon */
		secondarySourceType?: SecondarySource['type'];
		appearance?: 'large' | 'default' | 'minimal';
	}
	let { badge, secondarySourceType, appearance = 'default' }: Props = $props();

	const SECONDARY_SOURCE_ICONS: Record<SecondarySource['type'], Component> = {
		web: IconGlobe,
		fetched_url: IconGlobe,
		person: IconPerson,
		company: IconCompany,
		ifg_request: IconIfgRequest,
		poll: IconPoll
	};

	const wCtx = getWindowContext();
	const isDark = $derived(wCtx.isDark);
	const textColor = $derived(darkenColor(badge.color, isDark));
	const Icon = $derived(
		secondarySourceType ? SECONDARY_SOURCE_ICONS[secondarySourceType] : IconDocument
	);
</script>

<div
	style="background-color: {appearance !== 'minimal' && badge.color}; color: {textColor};"
	class="source-badge"
	class:secondarySource={secondarySourceType != null}
	class:large={appearance === 'large'}
	class:minimal={appearance === 'minimal'}
>
	<Icon />
	<span>
		{badge.index}{badge.subIndex ?? ''}
	</span>
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.source-badge {
		flex: 0 0 auto;
		font-size: 0.75rem;
		padding: 0.25rem 0.375rem 0.25rem 0.25rem;
		border-radius: 0.5rem;
		background: var(--surface-overlay-subtle);
		box-shadow: inset 0 0 0 1px var(--border-subtle);
		@include lib.inline-flex-row();
		@include lib.flex-align();
		gap: 0.25rem;
		font-size: 0.75rem;
		span {
			font-size: 0.75rem;
			line-height: 0.75rem;
		}

		&.large {
			font-size: 0.875rem;
			padding: 0.5rem 0.625rem 0.5rem 0.5rem;
			gap: 0.25rem;
			border-radius: 0.5rem;

			span {
				font-size: 0.875rem;
				line-height: 0.875rem;
			}
		}

		&.minimal {
			font-size: 0.75rem;
			padding: 0;
			gap: 0.125rem;
			border-radius: 0;
			box-shadow: none;
			background: transparent;
			vertical-align: middle;
			margin-right: 0.25rem;
			padding-bottom: 1px;

			span {
				font-size: 0.75rem;
				line-height: normal;
				font-weight: 500;
			}
		}
	}
</style>
